import { format } from 'date-fns'
import { comparer } from 'mobx'
import { applySnapshot, getSnapshot, Instance, types } from 'mobx-state-tree'

import { schemas, ZInfer } from '@showrunner/scrapi'

import { getRootStore } from '#state/models/getRootStore'
import { FolderItem, IFolder, IListing, WorkspaceItem } from '#state/types'
import { extractTsRestSuccess } from '#util/extractTsRest'
import { SortOrder } from '#util/LocalPersistence'
import { sortAlphaLower } from '#util/pgCollation'

import { BaseModel } from '../BaseModel'
import { sortAlphaByNameFn, sortByCreationFn, sortRecentFn } from '../util'

import {
  buildFolderListingsQuery,
  CursorRecord,
  getFolderSummaryQuery,
  isPagingStillValid,
  isPastCursor,
} from './helpers'

export { getFolderSummaryQuery }

const UncreatedFolder = types
  .model('UncreatedFolder', {
    name: '',
  })
  .actions((self) => ({
    setName(value: string) {
      self.name = value
    },
  }))

type FolderDetail = ZInfer<typeof schemas.FolderDetail>

const emojiPrefixed = (val: string) => /^\p{Emoji_Presentation}/gu.test(val)

export const Folder = BaseModel.named('Folder')
  .props({
    inTrash: types.boolean,
    isPrivate: types.boolean,
    id: types.identifier,
    name: types.string,
    parentId: types.string,
    uncreatedChild: types.maybe(UncreatedFolder),
    pendingLocationRequest: false,
    contents: types.maybe(
      types.model({
        scripts: types.number,
        rundowns: types.number,
        folders: types.number,
      }),
    ),
    cursorRecord: types.frozen<CursorRecord | undefined>(),
  })
  .views((self) => ({
    get isSelected(): boolean {
      return self.id === getRootStore(self).view.selectedFolderId
    },
    get isRootFolder() {
      return self.id === self.parentId
    },
    get isRootTrash() {
      return this.isRootFolder && self.inTrash && !self.isPrivate
    },
    get isPrivateRootTrash() {
      return this.isRootFolder && self.inTrash && self.isPrivate
    },
    get isSharedDash(): boolean {
      return this.isRootFolder && !self.isPrivate && !self.inTrash
    },
    get displayName(): string {
      if (this.isRootTrash) return 'Trash'
      if (this.isPrivateRootTrash) return 'Private trash'
      if (this.isRootFolder) return self.isPrivate ? 'Private' : 'Shared'
      return self.name
    },
    get sortOrder(): SortOrder {
      return getRootStore(self).user.getFolderSortOrder(self.id)
    },
    get unsortedDocuments(): IListing[] {
      // if this is the shared trash, get the children of any private trash root
      return getRootStore(self).getDocumentsInFolder(self.id)
    },
    get sortedDocuments(): IListing[] {
      // cant reference unsortedDocuments here because mst caches
      const documents = getRootStore(self).getDocumentsInFolder(self.id)
      switch (this.sortOrder) {
        case 'alphabetical':
          return documents.sort(sortAlphaByNameFn)
        case 'newest':
          return documents.sort(sortByCreationFn)
        case 'oldest':
          return documents.sort(sortByCreationFn).reverse()
        case 'recent':
        default:
          return documents.sort(sortRecentFn)
      }
    },
    get childFolders(): IFolder[] {
      const children = getRootStore(self).getChildFolders(self.id)
      // if this is the shared trash root, add the private trash root
      // as the first child
      if (this.isRootTrash && !self.isPrivate) {
        const { privateTrash } = getRootStore(self).rootFolders
        if (privateTrash) {
          children.unshift(privateTrash)
        }
      }
      return children
    },
    get parentFolder(): IFolder | undefined {
      return getRootStore(self).folderMap.get(self.parentId)
    },
    get path(): string {
      if (this.isSharedDash) {
        return '/'
      }
      return `/folders/${self.id}`
    },
    get treeDepth(): number {
      const parent = getRootStore(self).folderMap.get(self.parentId)
      // special case, we nest private root trash beneath shared trash
      if (this.isPrivateRootTrash) {
        return 1
      }
      if (!parent || parent.id === self.id) {
        return 0
      }
      return parent.treeDepth + 1
    },
    get isCreatingChild(): boolean {
      return getRootStore(self).view.folderCreatingChildId === self.id
    },
    get isBeingRenamed(): boolean {
      return getRootStore(self).view.folderBeingRenamedId === self.id
    },
    get documentCount(): number {
      if (!self.contents) {
        return 0
      }
      return Math.max(self.contents.scripts + self.contents.rundowns, 0)
    },
    // folders can be moved in/out of trash, but not their roots.
    // So, it's better to check up the tree than to rely on "inTrash"
    // being updated
    get belongsToTrashTree(): boolean {
      if (self.parentId === self.id) {
        return self.inTrash
      }
      return !!this.parentFolder?.inTrash
    },
    get trashFolder(): IFolder | undefined {
      return self.isPrivate
        ? getRootStore(self).rootFolders.privateTrash
        : getRootStore(self).rootFolders.sharedTrash
    },
  }))
  .extend((self) => {
    let folderItems: IListing[] = []

    return {
      views: {
        get cursorFilteredDocuments(): IListing[] {
          const { cursorRecord } = self
          if (!cursorRecord) {
            return []
          }

          const items = self.sortedDocuments.filter(
            (item) => !isPastCursor(cursorRecord, item, self.sortOrder),
          )
          if (comparer.structural(items, folderItems)) {
            return folderItems
          }
          folderItems = items
          return items
        },
      },
    }
  })
  // synchronous actions
  .actions((self) => ({
    // this unfortunate hack accounts for the fact that the version of icu
    // present on postgres installation dictates the vintage of emoji it recognizes
    // our local dev docker image only mis-sorts Unicode >=14 emoji
    // our production db mis-sorts Unicode >=11
    // https://www.reddit.com/r/TheSilphRoad/comments/73yhdq/comment/dnwqmfi/ 🫠
    // https://www.compart.com/en/unicode/U+100F6
    mungeCursorName(name: string): string {
      if (!emojiPrefixed(name)) return name
      if (self.sortOrder === 'alphabetical') {
        self.log.warn('missorted emoji-prefixed sort cursor encountered')
      }
      return `\u100F6 ${name}`
    },
    getRootFolder(isPrivate: boolean, inTrash: boolean): IFolder | undefined {
      return getRootStore(self).getRootFolder(isPrivate, inTrash)
    },
    startCreatingChild() {
      getRootStore(self).view.setCreatingChild(true)
      if (!self.uncreatedChild) {
        self.uncreatedChild = UncreatedFolder.create()
      }
    },
    startRenaming() {
      getRootStore(self).view.startRenamingFolder(self.id)
    },
    setName(name: string) {
      self.name = name
    },
    updateFromItem({ name, parentFolderId }: FolderItem) {
      self.name = name
      self.parentId = parentFolderId
    },
    updateFromDetail({ name, parentId, contents }: FolderDetail) {
      self.name = name
      self.parentId = parentId
      self.contents = contents
    },
    updateFields(result: Partial<FolderDetail>) {
      applySnapshot(self, { ...getSnapshot(self), ...result })
    },
    setCursorRecord(item: WorkspaceItem) {
      const { id, name, contentsModifiedAt, updatedAt, createdAt } = item

      self.cursorRecord = {
        uuid: id,
        name,
        mungedName: this.mungeCursorName(name),
        contentsModifiedAt,
        updatedAt,
        createdAt,
      }
    },
    clearCursorRecord() {
      self.cursorRecord = undefined
    },
  }))
  .actions((self) => ({
    setPendingLocationRequest(value: boolean) {
      self.pendingLocationRequest = value
    },
    async createRundown(
      name = `Rundown ${format(new Date(), 'MM/dd/yyyy')}`,
    ): Promise<{ rundownId: number }> {
      const params = { body: { name, folderId: self.id } }

      const {
        body: { id: rundownId },
      } = await extractTsRestSuccess(self.scrapi.rundowns.create(params), 200)

      const rowCount = 10
      const rowDataList: ZInfer<typeof schemas.RowDataInput>[] = []
      for (let i = 0; i < rowCount; i++) {
        rowDataList.push({ rowTypeId: 'element', blobData: {} })
      }

      await extractTsRestSuccess(
        self.scrapi.rundowns.insertRundownRows({
          params: { id: rundownId },
          body: {
            sequence: 1,
            rowDataList,
          },
        }),
        200,
      )
      return { rundownId }
    },

    async createSubfolder({ name }: { name: string }) {
      const result = await self.scrapi.folders.create({
        body: {
          name,
          parentId: self.id,
        },
      })

      if (result.status === 201) {
        getRootStore(self).ingestFolderList([result.body])
      }
      getRootStore(self).view.setCreatingChild(false)
    },

    async importScriptFromFdx(file: File): Promise<{ id: string } | undefined> {
      const formatId = getRootStore(self).currentOrg?.screenplayFormat.id

      const { body } = await extractTsRestSuccess(
        self.scrapi.scripts.importFDX({
          body: { fdx: file },
          query: { folder: self.id, formatId },
        }),
        200,
      )
      return body[0]
    },

    async moveToTrash() {
      const parentId = self.trashFolder?.id
      if (!parentId) return
      this.moveToFolder(parentId)
    },

    async moveToFolder(parentId: string) {
      const {
        body: { isPrivate, inTrash, name },
      } = await extractTsRestSuccess(
        self.scrapi.folders.moveFolder({
          params: { id: self.id },
          body: { folderId: parentId },
        }),
        200,
      )

      self.updateFields({
        isPrivate,
        inTrash,
        name,
        parentId,
      })
    },

    // this looks at the folder's sort order and cursor
    // and loads the next page of documents, ingests them,
    // then updates the cursor if it's still appropriate. We
    // use react-query for this so that multiple views/requests
    // for the same key can share promises/cached responses
    async loadPageOfDocuments() {
      const pageSize = getRootStore(self).view.listingsPageSize
      const { sortOrder, cursorRecord } = self
      const initialSortOrder = sortOrder

      const { queryFn, queryKey, paging } = buildFolderListingsQuery({
        folderId: self.id,
        scrapi: self.scrapi,
        pageSize,
        sortOrder,
        cursorRecord,
      })

      const result = await self.environment.queryClient.fetchQuery({
        queryFn,
        queryKey,
        staleTime: Infinity,
        gcTime: Infinity,
      })

      if (result.status !== 200) {
        return {
          success: false,
          result,
        }
      }
      // now we've got listings to load no matter what. The cursor
      // record MIGHT be stale though, so we check
      const shouldUpdateCursor =
        // the sort order should be the same
        initialSortOrder === self.sortOrder &&
        // And we shouldn't have invalidated the cursor in the
        // meantime
        isPagingStillValid({
          currentSortOrder: self.sortOrder,
          currentCursor: self.cursorRecord,
          fetchedPaging: paging,
        })

      getRootStore(self).ingestWorkspaceItems(result.body.data)
      if (shouldUpdateCursor) {
        const lastRecord = result.body.data[result.body.data.length - 1]
        if (lastRecord) {
          self.setCursorRecord(lastRecord)
        } else {
          // TODO: we should probably check that our cursor/counts
          // are correct. This might mean the user is seeing a defunct
          // "LOAD MORE" button.
        }
      }
      return {
        success: true,
        data: result.body.data,
      }
    },

    setSortOrder(order: SortOrder) {
      if (order !== self.sortOrder) {
        getRootStore(self).user.setFolderSortOrder(self.id, order)
        self.clearCursorRecord()
        this.loadPageOfDocuments()
      }
    },

    async rename(name: string) {
      await extractTsRestSuccess(
        self.scrapi.folders.updateFolder({
          params: { id: self.id },
          body: { name },
        }),
        200,
      )
      self.setName(name)
      getRootStore(self).view.stopRenamingFolder()
    },

    async refreshDetails() {
      const opts = getFolderSummaryQuery({
        folderId: self.id,
        scrapi: self.scrapi,
      })
      const response = await self.environment.queryClient.fetchQuery(opts)
      if (response.status === 200) {
        self.updateFromDetail(response.body)
        // sometimes we wind up with items below the cursor record but
        // none above, this triggers a refresh to avoid an empty folder with
        // a load more button
        if (
          self.cursorFilteredDocuments.length === 0 &&
          self.documentCount > 0
        ) {
          this.loadPageOfDocuments()
        }
      }
    },
  }))

export const sortFoldersAlphaFn = (
  a: Instance<typeof Folder>,
  b: Instance<typeof Folder>,
): number => {
  const diff = sortAlphaLower(a.displayName, b.displayName)
  if (diff === 0) {
    return a.id < b.id ? -1 : 1
  }

  return diff
}
